import { useMemo } from 'react'

import { matchFilterString, matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AddressModel, AddressRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['addresses']

type AddressFilters = {
  owner_id?: number,
  owner_type?: string,
  string?: string,
}

type UseAddressesOptions = {
  filters?: AddressFilters,
  performHttpRequests?: boolean,
  requestOptions?: AddressRequestOptions,
}

function useAddresses(options: UseAddressesOptions) {
  const { filters = {}, requestOptions } = options
  const {
    owner_id: filterOwnerId,
    owner_type: filterOwnerType,
    string: filterString,
  } = filters

  const {
    updatedEntities: { addresses: addressesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { addresses } = useSelector(reduxState => reduxState.entities)

  const filteredAddresses = useMemo(() => {
    const filtered = Object.values(addresses).filter((address: AddressModel) => {
      const {
        id, owner_id, owner_type, title,
      } = address

      const matchId = id?.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchOwnerId = matchFilterNumber(owner_id, filterOwnerId)
      const matchOwnerType = filterOwnerType ? owner_type === filterOwnerType : true

      return matchOwnerId && matchOwnerType && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [addressesUpdatedAt, JSON.stringify(filters)])

  const filteredAddressesCount = filteredAddresses.length
  const hasFilteredAddresses = !!filteredAddressesCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAddressesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loaded: loadedAddresses, loading: loadingAddresses } = useReduxAction(
    'addresses',
    'loadAddresses',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAddresses,
    filteredAddressesCount,
    hasFilteredAddresses,
    loaded: loadedAddresses,
    loading: loadingAddresses,
  }
}

export type UseAddressesPayload = ReturnType<typeof useAddresses>

export default useAddresses
